"use client";

import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import { useState, useEffect } from "react";
import Script from "next/script";
import {
  Container,
  Content,
  ContentWrapper,
  Title,
  Subtitle,
  ContactGrid,
  ContactCard,
  CardIcon,
  CardTitle,
  CardText,
  CardLink,
  FormSection,
  FormTitle,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  Select,
  SubmitButton,
  StatusMessage,
  FAQLink,
} from "./page.styles";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

  useEffect(() => {
    // Check if reCAPTCHA is already loaded
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
    }
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage(null);

    try {
      // Get reCAPTCHA token
      if (!window.grecaptcha || !recaptchaLoaded) {
        setStatusMessage({
          type: "error",
          text: "Security verification is loading. Please try again in a moment.",
        });
        setIsSubmitting(false);
        return;
      }

      const recaptchaToken = await window.grecaptcha.execute(
        RECAPTCHA_SITE_KEY,
        {
          action: "contact_form",
        }
      );

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatusMessage({
          type: "success",
          text: "Thank you for your message! We'll get back to you soon.",
        });
        // Reset form
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      } else {
        setStatusMessage({
          type: "error",
          text:
            data.error ||
            "Failed to send message. Please try again or email us directly at support@drizl.app",
        });
      }
    } catch {
      setStatusMessage({
        type: "error",
        text: "An error occurred. Please try again or email us directly at support@drizl.app",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
        onLoad={() => setRecaptchaLoaded(true)}
      />
      <PublicHeader />

      <Content>
        <ContentWrapper>
          <Title>Contact Us</Title>
          <Subtitle>
            Have a question or need assistance? We&apos;re here to help! Reach
            out to us through any of the methods below.
          </Subtitle>

          <ContactGrid>
            <ContactCard>
              <CardIcon>📧</CardIcon>
              <CardTitle>Email Support</CardTitle>
              <CardText>General inquiries and support</CardText>
              <CardLink href="mailto:support@drizl.app">
                support@drizl.app
              </CardLink>
            </ContactCard>
          </ContactGrid>

          <FormSection>
            <FormTitle>Send Us a Message</FormTitle>
            {statusMessage && (
              <StatusMessage $type={statusMessage.type}>
                {statusMessage.text}
              </StatusMessage>
            )}
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Subject *</Label>
                <Select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="">Select a subject...</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Order Support">Order Support</option>
                  <option value="Artisan Question">Artisan Question</option>
                  <option value="Technical Issue">Technical Issue</option>
                  <option value="Feedback">Feedback</option>
                  <option value="Partnership Opportunity">
                    Partnership Opportunity
                  </option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message *</Label>
                <TextArea
                  id="message"
                  name="message"
                  required
                  placeholder="Tell us how we can help you..."
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </SubmitButton>
            </Form>

            <FAQLink href="/faq">
              Looking for quick answers? Check out our <span>FAQ page</span>
            </FAQLink>
          </FormSection>

          <div
            style={{
              fontSize: "0.75rem",
              color: "#666",
              textAlign: "center",
              marginTop: "2rem",
            }}
          >
            This site is protected by reCAPTCHA and the Google{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C8553D" }}
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#C8553D" }}
            >
              Terms of Service
            </a>{" "}
            apply.
          </div>
        </ContentWrapper>
      </Content>

      <Footer />
    </Container>
  );
}
